import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bold, Italic, Heading2, Heading3, List, Quote, Link2, Eye, Edit3,
  Sparkles, ChevronRight, ChevronLeft, Save, Globe, Lock, ArrowLeft, X, Image as ImageIcon, Upload, Wand2, Loader2
} from "lucide-react";
import Navbar from "../components/Navbar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AI_ACTIONS = [
  { id: "draft", label: "Draft from idea", icon: "✦" },
  { id: "rewrite", label: "Rewrite for clarity", icon: "↻" },
  { id: "improve_tone", label: "Improve tone", icon: "▲" },
  { id: "proofread", label: "Proofread", icon: "✓" },
  { id: "generate_ideas", label: "Generate ideas", icon: "◈" },
  { id: "headline", label: "Headline suggestions", icon: "◉" },
  { id: "expand", label: "Expand text", icon: "⊞" },
  { id: "summarize", label: "Create excerpt", icon: "≡" },
];

const TONES = ["professional", "casual", "authoritative", "friendly", "persuasive"];

function insertHtmlAroundSelection(textarea, open, close) {
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const selected = textarea.value.substring(start, end);
  const before = textarea.value.substring(0, start);
  const after = textarea.value.substring(end);
  const newText = `${before}${open}${selected || "text"}${close}${after}`;
  textarea.value = newText;
  textarea.selectionStart = start + open.length;
  textarea.selectionEnd = start + open.length + (selected || "text").length;
  textarea.focus();
  return newText;
}

export default function AdminEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const textareaRef = useRef(null);

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [coverImage, setCoverImage] = useState("");
  const [tags, setTags] = useState("");
  const [status, setStatus] = useState("draft");
  const [visibility, setVisibility] = useState("free");
  const [tab, setTab] = useState("write"); // write | preview
  const [saving, setSaving] = useState(false);

  // Image insert modal
  const [imageOpen, setImageOpen] = useState(false);
  const [imageTab, setImageTab] = useState("upload"); // upload | url | generate
  const [imageUrl, setImageUrl] = useState("");
  const [imageAlt, setImageAlt] = useState("");
  const [imageCaption, setImageCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // AI image generation
  const [genPrompt, setGenPrompt] = useState("");
  const [genStyle, setGenStyle] = useState("");
  const [generating, setGenerating] = useState(false);
  const [genPreviewUrl, setGenPreviewUrl] = useState("");

  // Cover image upload
  const coverFileInputRef = useRef(null);
  const [coverUploading, setCoverUploading] = useState(false);

  // AI Sidebar
  const [aiOpen, setAiOpen] = useState(true);
  const [aiAction, setAiAction] = useState("draft");
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiTone, setAiTone] = useState("professional");
  const [aiOutput, setAiOutput] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  // Load existing post
  useEffect(() => {
    if (id) {
      axios.get(`${API}/admin/posts`, { withCredentials: true, headers })
        .then((r) => {
          const post = r.data.find((p) => p.id === id);
          if (post) {
            setTitle(post.title || "");
            setContent(post.content || "");
            setExcerpt(post.excerpt || "");
            setCoverImage(post.cover_image || "");
            setTags((post.tags || []).join(", "));
            setStatus(post.status || "draft");
            setVisibility(post.visibility || "free");
          }
        })
        .catch(() => toast.error("Failed to load post"));
    }
  }, [id]);

  const handleFormat = (open, close) => {
    if (!textareaRef.current) return;
    const newContent = insertHtmlAroundSelection(textareaRef.current, open, close);
    setContent(newContent);
  };

  const insertAtCursor = (snippet) => {
    const ta = textareaRef.current;
    if (!ta) {
      setContent((prev) => prev + (prev ? "\n\n" : "") + snippet);
      return;
    }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const before = ta.value.substring(0, start);
    const after = ta.value.substring(end);
    const newContent = `${before}${snippet}${after}`;
    setContent(newContent);
    // Restore cursor after snippet
    setTimeout(() => {
      ta.focus();
      ta.selectionStart = ta.selectionEnd = start + snippet.length;
    }, 0);
  };

  const buildImageHtml = (src, alt, caption) => {
    const safeAlt = (alt || "").replace(/"/g, "&quot;");
    if (caption && caption.trim()) {
      return `\n\n<figure>\n  <img src="${src}" alt="${safeAlt}" />\n  <figcaption>${caption}</figcaption>\n</figure>\n\n`;
    }
    return `\n\n<img src="${src}" alt="${safeAlt}" />\n\n`;
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image is too large. Please keep it under 8 MB.");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/admin/upload/image`, formData, {
        withCredentials: true,
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      const url = res.data.url;
      insertAtCursor(buildImageHtml(url, imageAlt || file.name.replace(/\.[^.]+$/, ""), imageCaption));
      toast.success("Image inserted!");
      setImageOpen(false);
      setImageAlt("");
      setImageCaption("");
      setImageUrl("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleImageUrl = () => {
    if (!imageUrl.trim()) {
      toast.error("Please paste an image URL");
      return;
    }
    insertAtCursor(buildImageHtml(imageUrl.trim(), imageAlt, imageCaption));
    toast.success("Image inserted!");
    setImageOpen(false);
    setImageUrl("");
    setImageAlt("");
    setImageCaption("");
  };

  const handleGenerateImage = async () => {
    if (!genPrompt.trim()) {
      toast.error("Please describe the image you want");
      return;
    }
    setGenerating(true);
    setGenPreviewUrl("");
    try {
      const res = await axios.post(
        `${API}/admin/ai/generate-image`,
        { prompt: genPrompt.trim(), style: genStyle.trim() },
        { withCredentials: true, headers, timeout: 90000 }
      );
      setGenPreviewUrl(res.data.url);
      toast.success("Image generated! Preview below.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed. Try rephrasing the prompt.");
    } finally {
      setGenerating(false);
    }
  };

  const insertGeneratedImage = () => {
    if (!genPreviewUrl) return;
    insertAtCursor(buildImageHtml(genPreviewUrl, imageAlt || genPrompt, imageCaption));
    toast.success("Image inserted!");
    setImageOpen(false);
    setGenPrompt("");
    setGenStyle("");
    setGenPreviewUrl("");
    setImageAlt("");
    setImageCaption("");
  };

  const handleCoverUpload = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image is too large. Please keep it under 8 MB.");
      return;
    }
    setCoverUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/admin/upload/image`, formData, {
        withCredentials: true,
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      setCoverImage(res.data.url);
      toast.success("Cover image uploaded!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setCoverUploading(false);
    }
  };

  const toolbarItems = [
    { label: "B", title: "Bold", action: () => handleFormat("<strong>", "</strong>") },
    { label: "I", title: "Italic", action: () => handleFormat("<em>", "</em>") },
    { label: "H2", title: "Heading 2", action: () => handleFormat("<h2>", "</h2>") },
    { label: "H3", title: "Heading 3", action: () => handleFormat("<h3>", "</h3>") },
    { label: <List size={15} />, title: "List item", action: () => handleFormat("<li>", "</li>") },
    { label: <Quote size={15} />, title: "Blockquote", action: () => handleFormat("<blockquote>", "</blockquote>") },
    { label: <Link2 size={15} />, title: "Link", action: () => handleFormat('<a href="URL">', "</a>") },
    { label: <ImageIcon size={15} />, title: "Insert image", action: () => setImageOpen(true) },
    { label: "<p>", title: "Paragraph", action: () => handleFormat("<p>", "</p>") },
  ];

  const handleSave = async (publishStatus = status) => {
    if (!title.trim()) { toast.error("Please add a title"); return; }
    setSaving(true);
    try {
      const payload = {
        title,
        content,
        excerpt,
        cover_image: coverImage,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
        status: publishStatus,
        visibility,
      };
      if (id) {
        await axios.put(`${API}/admin/posts/${id}`, payload, { withCredentials: true, headers });
        toast.success(publishStatus === "published" ? "Post published!" : "Draft saved.");
      } else {
        const res = await axios.post(`${API}/admin/posts`, payload, { withCredentials: true, headers });
        toast.success(publishStatus === "published" ? "Post published!" : "Draft saved.");
        navigate(`/admin/editor/${res.data.id}`, { replace: true });
      }
      setStatus(publishStatus);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save post");
    } finally {
      setSaving(false);
    }
  };

  const handleAiAssist = async () => {
    if (aiAction === "draft" && !aiPrompt && !title) {
      toast.error("Add a topic or title first");
      return;
    }
    setAiLoading(true);
    setAiOutput("");

    try {
      const payload = {
        action: aiAction,
        content: aiAction === "draft" ? (aiPrompt || title) : (content || aiPrompt),
        context: aiPrompt,
        tone: aiTone,
      };

      const response = await fetch(`${API}/admin/ai/assist`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulated = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n");
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const token = line.slice(6);
            if (token === "[DONE]") break;
            accumulated += token.replace(/\\n/g, "\n");
            setAiOutput(accumulated);
          }
        }
      }
    } catch (err) {
      toast.error("AI assistant failed. Please try again.");
    } finally {
      setAiLoading(false);
    }
  };

  const insertAiContent = () => {
    if (!aiOutput) return;
    setContent((prev) => prev + (prev ? "\n\n" : "") + aiOutput);
    setAiOutput("");
    toast.success("AI content inserted into editor");
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-20 h-screen flex flex-col">
        {/* Top Bar */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-border bg-background/90 backdrop-blur-sm shrink-0">
          <div className="flex items-center gap-3">
            <Link to="/admin/posts" className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
              <ArrowLeft size={18} />
            </Link>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Post title..."
              data-testid="post-title-input"
              className="font-cormorant text-2xl font-semibold text-foreground bg-transparent border-none outline-none placeholder:text-muted-foreground w-64 sm:w-96"
            />
          </div>

          <div className="flex items-center gap-2">
            {/* Visibility */}
            <button
              onClick={() => setVisibility(visibility === "free" ? "paid" : "free")}
              data-testid="visibility-toggle"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md font-manrope text-xs font-medium border transition-colors ${
                visibility === "paid"
                  ? "border-brand-accent text-brand-accent bg-accent/5"
                  : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {visibility === "paid" ? <Lock size={13} /> : <Globe size={13} />}
              {visibility === "paid" ? "Premium" : "Free"}
            </button>

            <button
              onClick={() => handleSave("draft")}
              disabled={saving}
              data-testid="save-draft-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border font-manrope text-sm text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-60 transition-colors"
            >
              <Save size={14} />
              Save draft
            </button>
            <button
              onClick={() => handleSave("published")}
              disabled={saving}
              data-testid="publish-btn"
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-brand-accent text-white font-manrope text-sm font-medium hover:bg-brand-accent-hover disabled:opacity-60 transition-colors"
            >
              <Globe size={14} />
              {status === "published" ? "Update" : "Publish"}
            </button>
          </div>
        </div>

        {/* Editor Body */}
        <div className="flex flex-1 overflow-hidden">
          {/* Main Editor */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Meta Bar */}
            <div className="px-4 sm:px-6 py-3 border-b border-border bg-background flex flex-wrap gap-3 items-center shrink-0">
              <div className="flex-1 min-w-48 flex gap-1 items-center">
                <input
                  type="text"
                  placeholder="Cover image URL (or upload →)"
                  value={coverImage}
                  onChange={(e) => setCoverImage(e.target.value)}
                  data-testid="cover-image-input"
                  className="flex-1 min-w-0 px-3 py-1.5 rounded-md border border-border bg-background font-manrope text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-brand-accent/40"
                />
                <input
                  ref={coverFileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                  data-testid="cover-file-input"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleCoverUpload(f);
                    e.target.value = "";
                  }}
                />
                <button
                  type="button"
                  onClick={() => coverFileInputRef.current?.click()}
                  disabled={coverUploading}
                  title="Upload cover image"
                  data-testid="cover-upload-btn"
                  className="shrink-0 p-1.5 rounded-md border border-border hover:border-brand-accent hover:bg-brand-accent/10 text-foreground transition-colors disabled:opacity-50"
                >
                  {coverUploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                </button>
              </div>
              <input
                type="text"
                placeholder="Tags (comma separated)"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                data-testid="tags-input"
                className="flex-1 min-w-48 px-3 py-1.5 rounded-md border border-border bg-background font-manrope text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-brand-accent/40"
              />
              <input
                type="text"
                placeholder="Excerpt (optional)"
                value={excerpt}
                onChange={(e) => setExcerpt(e.target.value)}
                data-testid="excerpt-input"
                className="flex-1 min-w-48 px-3 py-1.5 rounded-md border border-border bg-background font-manrope text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-brand-accent/40"
              />
            </div>

            {/* Tab Bar + Toolbar */}
            <div className="px-4 sm:px-6 py-2 border-b border-border flex items-center gap-4 bg-background shrink-0 editor-toolbar">
              <div className="flex items-center gap-1 mr-3">
                <button onClick={() => setTab("write")} data-testid="write-tab"
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md font-manrope text-sm transition-colors ${tab === "write" ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                  <Edit3 size={14} /> Write
                </button>
                <button onClick={() => setTab("preview")} data-testid="preview-tab"
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md font-manrope text-sm transition-colors ${tab === "preview" ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                  <Eye size={14} /> Preview
                </button>
              </div>

              {tab === "write" && (
                <div className="flex items-center gap-0.5 flex-wrap">
                  {toolbarItems.map((item, i) => (
                    <button key={i} onClick={item.action} title={item.title}
                      className="px-2.5 py-1.5 rounded-md font-manrope text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors min-w-8 text-center">
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto">
              {tab === "write" ? (
                <textarea
                  ref={textareaRef}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Start writing your post... (HTML supported)

<h2>Your Section Title</h2>
<p>Your paragraph text goes here...</p>
<ul>
  <li>List item one</li>
  <li>List item two</li>
</ul>"
                  data-testid="post-content-editor"
                  className="w-full h-full p-6 sm:p-10 bg-background text-foreground font-manrope text-sm leading-relaxed resize-none border-none outline-none placeholder:text-muted-foreground/50"
                />
              ) : (
                <div className="p-6 sm:p-10 max-w-2xl mx-auto">
                  {coverImage && (
                    <div className="aspect-[16/9] rounded-lg overflow-hidden mb-8">
                      <img src={coverImage} alt="Cover" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <h1 className="font-cormorant text-4xl font-semibold text-foreground mb-6">{title || "Untitled Post"}</h1>
                  {content ? (
                    <div className="article-content text-foreground" dangerouslySetInnerHTML={{ __html: content }} />
                  ) : (
                    <p className="font-manrope text-sm text-muted-foreground">Nothing to preview yet. Start writing!</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* AI Sidebar */}
          <AnimatePresence>
            {aiOpen && (
              <motion.aside
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: 320, opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="border-l border-border bg-card flex flex-col overflow-hidden shrink-0"
                data-testid="ai-sidebar"
              >
                <div className="p-4 border-b border-border flex items-center justify-between shrink-0">
                  <div className="flex items-center gap-2">
                    <Sparkles size={16} className="text-brand-accent" />
                    <span className="font-manrope text-sm font-semibold text-foreground">AI Assistant</span>
                  </div>
                  <button onClick={() => setAiOpen(false)} className="p-1 rounded-md text-muted-foreground hover:text-foreground transition-colors">
                    <X size={16} />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {/* Action Selector */}
                  <div>
                    <p className="font-manrope text-xs font-semibold text-foreground mb-2">What to do</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {AI_ACTIONS.map((action) => (
                        <button
                          key={action.id}
                          onClick={() => setAiAction(action.id)}
                          data-testid={`ai-action-${action.id}`}
                          className={`flex items-center gap-1.5 px-2.5 py-2 rounded-lg font-manrope text-xs font-medium transition-colors text-left ${
                            aiAction === action.id
                              ? "bg-brand-accent text-white"
                              : "bg-muted text-foreground hover:bg-muted/80"
                          }`}
                        >
                          <span className="text-base leading-none">{action.icon}</span>
                          {action.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Tone (for improve_tone) */}
                  {aiAction === "improve_tone" && (
                    <div>
                      <p className="font-manrope text-xs font-semibold text-foreground mb-2">Target tone</p>
                      <select
                        value={aiTone}
                        onChange={(e) => setAiTone(e.target.value)}
                        data-testid="ai-tone-select"
                        className="w-full px-3 py-2 rounded-lg border border-border bg-background font-manrope text-sm focus:outline-none"
                      >
                        {TONES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  )}

                  {/* Custom Prompt */}
                  <div>
                    <p className="font-manrope text-xs font-semibold text-foreground mb-2">
                      {aiAction === "draft" ? "Topic or idea" : "Additional context (optional)"}
                    </p>
                    <textarea
                      value={aiPrompt}
                      onChange={(e) => setAiPrompt(e.target.value)}
                      placeholder={aiAction === "draft" ? "e.g. 5 tips for writing killer CTAs..." : "e.g. make it more conversational..."}
                      rows={3}
                      data-testid="ai-prompt-input"
                      className="w-full px-3 py-2 rounded-lg border border-border bg-background font-manrope text-sm resize-none focus:outline-none focus:ring-1 focus:ring-brand-accent/40"
                    />
                  </div>

                  <button
                    onClick={handleAiAssist}
                    disabled={aiLoading}
                    data-testid="ai-generate-btn"
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-full bg-brand-accent text-white font-manrope text-sm font-medium hover:bg-brand-accent-hover disabled:opacity-60 transition-colors"
                  >
                    <Sparkles size={14} />
                    {aiLoading ? "Generating..." : "Generate"}
                  </button>

                  {/* Output */}
                  {aiOutput && (
                    <div className="border border-border rounded-lg overflow-hidden">
                      <div className="p-3 max-h-72 overflow-y-auto">
                        <p className="font-manrope text-xs text-foreground whitespace-pre-wrap leading-relaxed">{aiOutput}</p>
                      </div>
                      <div className="border-t border-border p-2 flex gap-2">
                        <button
                          onClick={insertAiContent}
                          data-testid="ai-insert-btn"
                          className="flex-1 py-1.5 rounded-md bg-brand-accent text-white font-manrope text-xs font-medium hover:bg-brand-accent-hover transition-colors"
                        >
                          Insert into editor
                        </button>
                        <button
                          onClick={() => setAiOutput("")}
                          className="px-3 py-1.5 rounded-md border border-border font-manrope text-xs text-muted-foreground hover:text-foreground transition-colors"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </motion.aside>
            )}
          </AnimatePresence>

          {/* Toggle AI sidebar button */}
          {!aiOpen && (
            <button
              onClick={() => setAiOpen(true)}
              data-testid="ai-toggle-btn"
              className="fixed right-0 top-1/2 -translate-y-1/2 z-40 flex items-center gap-1.5 px-2 py-4 rounded-l-xl bg-brand-accent text-white font-manrope text-xs font-medium shadow-md hover:bg-brand-accent-hover transition-colors"
            >
              <Sparkles size={14} />
              <span className="writing-mode-vertical" style={{ writingMode: "vertical-rl" }}>AI</span>
            </button>
          )}
        </div>
      </main>

      {/* Image Insert Modal */}
      <AnimatePresence>
        {imageOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
            onClick={() => !uploading && setImageOpen(false)}
            data-testid="image-modal"
          >
            <motion.div
              initial={{ scale: 0.95, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              transition={{ duration: 0.15 }}
              className="w-full max-w-lg bg-background rounded-2xl border border-border shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                <div className="flex items-center gap-2">
                  <ImageIcon size={18} className="text-brand-accent" />
                  <h3 className="font-cormorant text-xl font-semibold text-foreground">Insert an image</h3>
                </div>
                <button
                  onClick={() => !uploading && setImageOpen(false)}
                  className="p-1 rounded-md text-muted-foreground hover:text-foreground transition-colors"
                  disabled={uploading}
                  data-testid="image-modal-close"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Tabs */}
              <div className="px-5 pt-4 flex items-center gap-1 border-b border-border overflow-x-auto">
                <button
                  onClick={() => setImageTab("upload")}
                  data-testid="image-tab-upload"
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-t-md font-manrope text-sm transition-colors whitespace-nowrap ${
                    imageTab === "upload"
                      ? "text-foreground border-b-2 border-brand-accent -mb-px"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Upload size={14} /> Upload
                </button>
                <button
                  onClick={() => setImageTab("url")}
                  data-testid="image-tab-url"
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-t-md font-manrope text-sm transition-colors whitespace-nowrap ${
                    imageTab === "url"
                      ? "text-foreground border-b-2 border-brand-accent -mb-px"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Link2 size={14} /> Paste URL
                </button>
                <button
                  onClick={() => setImageTab("generate")}
                  data-testid="image-tab-generate"
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-t-md font-manrope text-sm transition-colors whitespace-nowrap ${
                    imageTab === "generate"
                      ? "text-foreground border-b-2 border-brand-accent -mb-px"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Wand2 size={14} /> Generate with AI
                </button>
              </div>

              <div className="p-5 space-y-4">
                {imageTab === "upload" ? (
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      className="hidden"
                      data-testid="image-file-input"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleImageUpload(f);
                        e.target.value = "";
                      }}
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      data-testid="image-upload-btn"
                      className="w-full py-10 rounded-xl border-2 border-dashed border-border hover:border-brand-accent hover:bg-accent/5 transition-colors flex flex-col items-center gap-2 disabled:opacity-60"
                    >
                      <Upload size={28} className="text-brand-accent" />
                      <span className="font-manrope text-sm font-medium text-foreground">
                        {uploading ? "Uploading..." : "Choose an image from your computer"}
                      </span>
                      <span className="font-manrope text-xs text-muted-foreground">
                        JPG, PNG, GIF, or WEBP — up to 8 MB
                      </span>
                    </button>
                  </div>
                ) : imageTab === "url" ? (
                  <div>
                    <label className="block font-manrope text-xs font-semibold text-foreground mb-1.5">
                      Image URL
                    </label>
                    <input
                      type="url"
                      value={imageUrl}
                      onChange={(e) => setImageUrl(e.target.value)}
                      placeholder="https://example.com/photo.jpg"
                      data-testid="image-url-input"
                      className="w-full px-3 py-2 rounded-md border border-border bg-background font-manrope text-sm focus:outline-none focus:ring-1 focus:ring-brand-accent/40"
                    />
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <label className="block font-manrope text-xs font-semibold text-foreground mb-1.5">
                        Describe the image you want
                      </label>
                      <textarea
                        value={genPrompt}
                        onChange={(e) => setGenPrompt(e.target.value)}
                        placeholder="e.g. a steaming mug of tea on a wooden writing desk, open notebook with handwritten notes, soft morning light through a window"
                        rows={3}
                        data-testid="image-gen-prompt"
                        className="w-full px-3 py-2 rounded-md border border-border bg-background font-manrope text-sm focus:outline-none focus:ring-1 focus:ring-brand-accent/40 resize-none"
                      />
                    </div>
                    <div>
                      <label className="block font-manrope text-xs font-semibold text-foreground mb-1.5">
                        Style <span className="text-muted-foreground font-normal">(optional)</span>
                      </label>
                      <input
                        type="text"
                        value={genStyle}
                        onChange={(e) => setGenStyle(e.target.value)}
                        placeholder="e.g. editorial photograph, warm tones, film-like"
                        data-testid="image-gen-style"
                        className="w-full px-3 py-2 rounded-md border border-border bg-background font-manrope text-sm focus:outline-none focus:ring-1 focus:ring-brand-accent/40"
                      />
                    </div>

                    {genPreviewUrl ? (
                      <div className="rounded-xl overflow-hidden border border-border bg-muted/40">
                        <img src={genPreviewUrl} alt="Generated preview" className="w-full h-auto block" />
                      </div>
                    ) : null}

                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleGenerateImage}
                        disabled={generating || !genPrompt.trim()}
                        data-testid="image-gen-btn"
                        className="flex-1 py-2.5 rounded-full bg-brand-accent text-white font-manrope text-sm font-medium hover:bg-brand-accent-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                      >
                        {generating ? (
                          <>
                            <Loader2 size={14} className="animate-spin" /> Generating… (~15 sec)
                          </>
                        ) : (
                          <>
                            <Wand2 size={14} /> {genPreviewUrl ? "Regenerate" : "Generate image"}
                          </>
                        )}
                      </button>
                      {genPreviewUrl && !generating && (
                        <button
                          onClick={insertGeneratedImage}
                          data-testid="image-gen-insert-btn"
                          className="py-2.5 px-5 rounded-full border border-brand-accent text-brand-accent font-manrope text-sm font-medium hover:bg-brand-accent/5 transition-colors"
                        >
                          Insert
                        </button>
                      )}
                    </div>
                    <p className="font-manrope text-[11px] text-muted-foreground leading-relaxed">
                      Powered by Gemini Nano Banana. Costs ~1¢ per image on your Emergent LLM key.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <label className="block font-manrope text-xs font-semibold text-foreground mb-1.5">
                      Alt text <span className="text-muted-foreground font-normal">(for accessibility & SEO)</span>
                    </label>
                    <input
                      type="text"
                      value={imageAlt}
                      onChange={(e) => setImageAlt(e.target.value)}
                      placeholder="e.g. laptop and coffee on a desk"
                      data-testid="image-alt-input"
                      className="w-full px-3 py-2 rounded-md border border-border bg-background font-manrope text-sm focus:outline-none focus:ring-1 focus:ring-brand-accent/40"
                    />
                  </div>
                  <div>
                    <label className="block font-manrope text-xs font-semibold text-foreground mb-1.5">
                      Caption <span className="text-muted-foreground font-normal">(optional, shown under image)</span>
                    </label>
                    <input
                      type="text"
                      value={imageCaption}
                      onChange={(e) => setImageCaption(e.target.value)}
                      placeholder="e.g. Photo by Elizabeth Neal"
                      data-testid="image-caption-input"
                      className="w-full px-3 py-2 rounded-md border border-border bg-background font-manrope text-sm focus:outline-none focus:ring-1 focus:ring-brand-accent/40"
                    />
                  </div>
                </div>

                {imageTab === "url" && (
                  <button
                    onClick={handleImageUrl}
                    data-testid="image-insert-url-btn"
                    className="w-full py-2.5 rounded-full bg-brand-accent text-white font-manrope text-sm font-medium hover:bg-brand-accent-hover transition-colors"
                  >
                    Insert image
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
